import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../common/Table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  ScreenTag,
  ScreenTagFormData,
  ScreenTagUpdateFormData,
  ScreenTagRequest,
} from "../../schemas/screenTagSchema";
import { useScreenTagsInfinite } from "../../hooks/useScreenTagsInfinite";
import ScreenTagForm from "../forms/ScreenTagForm";

const ScreenTags: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<ScreenTag | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    screenTags,
    isLoading: isScreenTagsLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createScreenTag,
    updateScreenTag,
    deleteScreenTags,
  } = useScreenTagsInfinite({ search });

  const handleEdit = (tag: ScreenTag) => {
    setSelectedTag(tag);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (tag: ScreenTag) => {
    if (window.confirm(`Delete Screen Tag "${tag.name}"?`)) {
      await deleteScreenTags([tag.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (window.confirm(`Delete ${ids.length} selected screen tag(s)?`)) {
      deleteScreenTags(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (
    data: ScreenTagFormData | ScreenTagUpdateFormData,
  ) => {
    setIsSubmitting(true);
    try {
      if (selectedTag?.id) {
        await updateScreenTag({
          id: selectedTag.id,
          screenTag: data as ScreenTagUpdateFormData,
        });
      } else {
        const payload: ScreenTagRequest = {
          name: (data as ScreenTagFormData).name,
          description: (data as ScreenTagFormData).description,
        };
        await createScreenTag(payload);
      }
    } finally {
      setSelectedTag(undefined);
      setIsFormModalOpen(false);
      setIsSubmitting(false);
    }
  };

  const isSystemTag = (tag: ScreenTag) => !!tag.type?.startsWith("system_");

  const columns: ColumnDef<ScreenTag>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "description", header: "Description" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      disabled: isSystemTag,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      disabled: isSystemTag,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Screen Tags</h1>
        <button
          onClick={() => {
            setIsFormModalOpen(true);
          }}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={screenTags as ScreenTag[]}
        actions={actions}
        isLoading={isScreenTagsLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable={true}
        getRowId={(tag) => tag.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <ScreenTagForm
          isOpen={isFormModalOpen}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedTag(undefined);
          }}
          initialData={selectedTag}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedTag}
        />
      )}
    </>
  );
};

export default ScreenTags;
